import { useState, useCallback, useEffect } from 'react';
import {
  OnboardingFormData,
  PersonaFisicaForm as PFData,
  PessoaJuridicaForm as PJData,
  CreditQuestionnaireForm as CreditData,
  AuthorizationsForm,
} from '../types';

// Textos das autorizações exibidos no step de autorizações
export const AUTHORIZATIONS = [
  {
    key: 'scr',
    title: 'Consulta SCR e Órgãos de Proteção ao Crédito',
    description:
      'Autorizo a consulta aos sistemas SCR, Serasa, SPC e demais órgãos de proteção ao crédito.',
  },
  {
    key: 'dataSharing',
    title: 'Compartilhamento de Dados',
    description:
      'Autorizo o compartilhamento de dados com subsidiárias, controladas e afiliadas do Banco Cargill.',
  },
  {
    key: 'foreignExchange',
    title: 'Consulta de Operações de Câmbio',
    description:
      'Autorizo a consulta de informações sobre operações de câmbio junto ao Banco Central.',
  },
  {
    key: 'ccs',
    title: 'CCS e Portal Siscomex',
    description:
      'Autorizo o fornecimento de dados ao Cadastro Central do Sistema Financeiro Nacional.',
  },
  {
    key: 'antiCorruption',
    title: 'Declaração Anticorrupção e PLD',
    description:
      'Declaro conformidade com políticas anticorrupção e prevenção à lavagem de dinheiro.',
  },
  {
    key: 'callRecording',
    title: 'Gravação de Chamadas',
    description: 'Tenho ciência de que as ligações telefônicas poderão ser gravadas.',
  },
  {
    key: 'derivatives',
    title: 'Consulta CED (Derivativos)',
    description:
      'Autorizo consulta à Central de Exposição de Derivativos controlada pela Febraban.',
  },
  {
    key: 'fraudPrevention',
    title: 'Prevenção de Fraudes',
    description:
      'Autorizo o compartilhamento de dados para prevenção de fraudes no Sistema Financeiro.',
  },
  {
    key: 'lgpd',
    title: 'LGPD - Lei Geral de Proteção de Dados',
    description: 'Declaro conhecer e ser aderente à LGPD.',
  },
  {
    key: 'registrationUpdate',
    title: 'Atualização Cadastral',
    description:
      'Estou ciente de que a falta de atualização cadastral pode ensejar bloqueio da conta.',
  },
] as const;

export interface UseFormDataReturn {
  formData: Partial<OnboardingFormData>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<OnboardingFormData>>>;
  handlePFChange: (data: Partial<PFData>) => void;
  handlePJChange: (data: Partial<PJData>) => void;
  handleCreditChange: (data: Partial<CreditData>) => void;
  handleAuthorizationChange: (key: keyof AuthorizationsForm) => (value: boolean) => void;
  handleAcceptAll: (checked: boolean) => void;
}

/**
 * Hook para gerenciar os dados do formulário e handlers de alteração.
 */
export function useFormData(
  initialData?: Partial<OnboardingFormData> | null
): UseFormDataReturn {
  const [formData, setFormData] = useState<Partial<OnboardingFormData>>({
    customerType: 'PF',
    pessoaFisica: {} as PFData,
    pessoaJuridica: {} as PJData,
    creditQuestionnaire: {} as CreditData,
    authorizations: {
      scr: false,
      dataSharing: false,
      foreignExchange: false,
      ccs: false,
      antiCorruption: false,
      callRecording: false,
      derivatives: false,
      fraudPrevention: false,
      lgpd: false,
      registrationUpdate: false,
      allAccepted: false,
      acceptanceDate: '',
      ipAddress: '',
    },
    documents: { documents: [] },
    status: 'draft',
  });

  // Atualizar formData quando dados iniciais (vindos da validação do token) estiverem disponíveis
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  const handlePFChange = useCallback((data: Partial<PFData>) => {
    setFormData((prev) => ({
      ...prev,
      pessoaFisica: data as PFData,
    }));
  }, []);

  const handlePJChange = useCallback((data: Partial<PJData>) => {
    setFormData((prev) => ({
      ...prev,
      pessoaJuridica: data as PJData,
    }));
  }, []);

  const handleCreditChange = useCallback((data: Partial<CreditData>) => {
    setFormData((prev) => ({
      ...prev,
      creditQuestionnaire: data as CreditData,
    }));
  }, []);

  const handleAuthorizationChange = useCallback(
    (key: keyof AuthorizationsForm) => (value: boolean) => {
      setFormData((prev) => {
        const newAuthorizations = {
          ...prev.authorizations,
          [key]: value,
        } as AuthorizationsForm;

        // Verificar se todas as autorizações foram aceitas
        const allKeys = AUTHORIZATIONS.map((a) => a.key) as (keyof AuthorizationsForm)[];
        const allAccepted = allKeys.every(
          (k) => newAuthorizations[k as keyof AuthorizationsForm] === true
        );

        return {
          ...prev,
          authorizations: {
            ...newAuthorizations,
            allAccepted,
          },
        };
      });
    },
    []
  );

  const handleAcceptAll = useCallback((checked: boolean) => {
    setFormData((prev) => {
      const newAuthorizations: AuthorizationsForm = {
        scr: checked,
        dataSharing: checked,
        foreignExchange: checked,
        ccs: checked,
        antiCorruption: checked,
        callRecording: checked,
        derivatives: checked,
        fraudPrevention: checked,
        lgpd: checked,
        registrationUpdate: checked,
        allAccepted: checked,
        acceptanceDate: checked ? new Date().toISOString() : '',
        ipAddress: '',
      };

      return {
        ...prev,
        authorizations: newAuthorizations,
      };
    });
  }, []);

  return {
    formData,
    setFormData,
    handlePFChange,
    handlePJChange,
    handleCreditChange,
    handleAuthorizationChange,
    handleAcceptAll,
  };
}
